import React, { useMemo, useState } from "react";
import { MapPin, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import {
    ComposableMap,
    Geographies,
    Geography,
    Marker,
} from "react-simple-maps";
import { PRODUCTS } from "@/data/products";
import indiaGeo from "@/data/india.geo.json";

// City → [longitude, latitude] (Geographic projection coords)
const SOURCING = {
    "onion-powder":       { city: "Nashik",      state: "Maharashtra",      coords: [73.79, 19.99] },
    "garlic-powder":      { city: "Gadag",       state: "Karnataka",        coords: [75.62, 15.42] },
    "tomato-powder":      { city: "Nashik",      state: "Maharashtra",      coords: [73.79, 19.99] },
    "carrot-powder":      { city: "Ooty",        state: "Tamil Nadu",       coords: [76.69, 11.41] },
    "spinach-powder":     { city: "Palampur",    state: "Himachal Pradesh", coords: [76.54, 32.11] },
    "beetroot-powder":    { city: "Alwar",       state: "Rajasthan",        coords: [76.63, 27.55] },
    "ginger-powder":      { city: "Wayanad",     state: "Kerala",           coords: [76.13, 11.69] },
    "potato-powder":      { city: "Agra",        state: "Uttar Pradesh",    coords: [78.00, 27.17] },
    "banana-powder":      { city: "Theni",       state: "Tamil Nadu",       coords: [77.48, 10.01] },
    "mango-powder":       { city: "Ratnagiri",   state: "Maharashtra",      coords: [73.31, 16.99] },
    "apple-powder":       { city: "Kinnaur",     state: "Himachal Pradesh", coords: [78.45, 31.55] },
    "guava-powder":       { city: "Allahabad",   state: "Uttar Pradesh",    coords: [81.85, 25.45] },
    "papaya-powder":      { city: "Meerut",      state: "Uttar Pradesh",    coords: [77.70, 28.98] },
    "pineapple-powder":   { city: "Vazhakulam",  state: "Kerala",           coords: [76.62, 9.91]  },
    "lemon-powder":       { city: "Vijayapura",  state: "Karnataka",        coords: [75.71, 16.82] },
    "jackfruit-powder":   { city: "Kollam",      state: "Kerala",           coords: [76.61, 8.89]  },
    "ashwagandha-powder": { city: "Neemuch",     state: "Madhya Pradesh",   coords: [74.86, 24.47] },
    "moringa-powder":     { city: "Tirunelveli", state: "Tamil Nadu",       coords: [77.69, 8.71]  },
    "red-chilli-powder":  { city: "Guntur",      state: "Andhra Pradesh",   coords: [80.45, 16.31] },
    "green-chilli-powder":{ city: "Jalgaon",     state: "Maharashtra",      coords: [75.56, 21.00] },
    "green-chilli-flakes":{ city: "Jalgaon",     state: "Maharashtra",      coords: [75.56, 21.00] },
};

const buildGroups = () => {
    const map = new Map();
    for (const p of PRODUCTS) {
        const src = SOURCING[p.id];
        if (!src) continue;
        const key = `${src.city}-${src.state}`;
        if (!map.has(key)) {
            map.set(key, { ...src, products: [] });
        }
        map.get(key).products.push(p);
    }
    return [...map.values()];
};

const SourcingMap = () => {
    const groups = useMemo(buildGroups, []);
    const [activeKey, setActiveKey] = useState(null);

    return (
        <section
            id="sourcing"
            data-testid="sourcing-section"
            className="py-24 md:py-36 bg-sun-bg relative overflow-hidden"
        >
            <div className="absolute -top-40 -right-40 w-[36rem] h-[36rem] sun-rays opacity-10 animate-spin-slow pointer-events-none" />

            <div className="container-x relative">
                <div className="grid grid-cols-12 gap-6 md:gap-12 mb-10 md:mb-14">
                    <div className="col-span-12 md:col-span-7">
                        <div className="overline mb-4">— Sourced from India</div>
                        <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-[-0.02em] text-sun-ink">
                            From these
                            <br />
                            <span className="display-italic text-gradient">soils, with care.</span>
                        </h2>
                        <p className="mt-6 text-sun-inkSoft text-base md:text-lg leading-relaxed max-w-xl">
                            Every Ostrich pouch starts in a specific village or town across India. We
                            partner directly with farmer-clusters so each SKU has a real origin — never
                            a generic "blended" anything.
                        </p>
                    </div>
                    <div className="col-span-12 md:col-span-5 flex md:justify-end items-end">
                        <div className="bg-white border border-sun-line rounded-3xl px-6 py-5 flex items-center gap-4">
                            <div className="w-11 h-11 rounded-2xl bg-sunrise text-white flex items-center justify-center">
                                <Sparkles className="w-5 h-5" strokeWidth={2} />
                            </div>
                            <div>
                                <div className="font-heading text-3xl text-sun-ink leading-none">
                                    {groups.length}
                                </div>
                                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-sun-muted mt-1">
                                    farming regions
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-12 gap-4 md:gap-8 items-start">
                    {/* Map */}
                    <div className="col-span-12 md:col-span-7 relative">
                        <div className="relative bg-white border border-sun-line rounded-3xl p-3 md:p-6 overflow-hidden">
                            <div className="absolute -top-12 -left-12 w-72 h-72 rounded-full bg-sun-gold/20 blur-3xl pointer-events-none" />

                            <ComposableMap
                                projection="geoMercator"
                                projectionConfig={{
                                    scale: 1000,
                                    center: [82, 22],
                                }}
                                width={500}
                                height={580}
                                style={{ width: "100%", height: "auto" }}
                            >
                                <Geographies geography={indiaGeo}>
                                    {({ geographies }) =>
                                        geographies.map((geo) => (
                                            <Geography
                                                key={geo.rsmKey}
                                                geography={geo}
                                                fill="#FFEFD9"
                                                stroke="#F4571F"
                                                strokeWidth={0.5}
                                                style={{
                                                    default: { outline: "none" },
                                                    hover: { fill: "#FFE0BB", outline: "none" },
                                                    pressed: { outline: "none" },
                                                }}
                                            />
                                        ))
                                    }
                                </Geographies>

                                {groups.map((g) => {
                                    const key = `${g.city}-${g.state}`;
                                    const active = activeKey === key;
                                    return (
                                        <Marker
                                            key={key}
                                            coordinates={g.coords}
                                            data-testid={`map-pin-${key.toLowerCase().replace(/\s/g, "-")}`}
                                            onMouseEnter={() => setActiveKey(key)}
                                            onMouseLeave={() => setActiveKey(null)}
                                            onClick={() => setActiveKey(active ? null : key)}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <circle
                                                r={active ? 16 : 9}
                                                fill="#F4571F"
                                                opacity="0.22"
                                                style={{ transition: "all 0.3s" }}
                                            />
                                            <circle
                                                r={active ? 7 : 4}
                                                fill="#F4571F"
                                                stroke="#fff"
                                                strokeWidth="1.5"
                                                style={{ transition: "all 0.3s" }}
                                            />
                                            {active && (
                                                <g>
                                                    <rect
                                                        x={10}
                                                        y={-24}
                                                        width={Math.max(70, g.city.length * 6 + 18)}
                                                        height={20}
                                                        rx="5"
                                                        fill="#0F2A3F"
                                                    />
                                                    <text
                                                        x={18}
                                                        y={-10}
                                                        fill="#FFD27A"
                                                        fontSize="9"
                                                        fontFamily="ui-monospace, monospace"
                                                        letterSpacing="1.2"
                                                        fontWeight="600"
                                                    >
                                                        {g.city.toUpperCase()}
                                                    </text>
                                                </g>
                                            )}
                                        </Marker>
                                    );
                                })}
                            </ComposableMap>

                            <div className="mt-2 text-center font-mono text-[9px] uppercase tracking-[0.22em] text-sun-muted">
                                Hover or tap any pin
                            </div>
                        </div>
                    </div>

                    {/* List */}
                    <div className="col-span-12 md:col-span-5">
                        <div className="overline mb-4">— {groups.length} regions · 20 SKUs</div>
                        <div className="space-y-2 max-h-[520px] md:max-h-[560px] overflow-y-auto pr-2">
                            {groups
                                .slice()
                                .sort((a, b) => a.city.localeCompare(b.city))
                                .map((g) => {
                                    const key = `${g.city}-${g.state}`;
                                    const active = activeKey === key;
                                    return (
                                        <div
                                            key={key}
                                            onMouseEnter={() => setActiveKey(key)}
                                            onMouseLeave={() => setActiveKey(null)}
                                            data-testid={`sourcing-row-${key.toLowerCase().replace(/\s/g, "-")}`}
                                            className={`bg-white border rounded-2xl p-4 transition-all ${
                                                active
                                                    ? "border-sun-orange shadow-md"
                                                    : "border-sun-line"
                                            }`}
                                        >
                                            <div className="flex items-start gap-3">
                                                <div
                                                    className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                                                        active
                                                            ? "bg-sunrise text-white"
                                                            : "bg-sun-cream text-sun-orange"
                                                    }`}
                                                >
                                                    <MapPin className="w-4 h-4" strokeWidth={2} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-heading text-lg text-sun-ink leading-tight">
                                                        {g.city}
                                                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-sun-muted ml-2">
                                                            {g.state}
                                                        </span>
                                                    </div>
                                                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                                                        {g.products.map((p) => (
                                                            <Link
                                                                key={p.id}
                                                                to={`/product/${p.id}`}
                                                                className="text-[11px] font-mono uppercase tracking-[0.12em] px-2 py-0.5 rounded-full bg-sun-cream text-sun-ink hover:bg-sun-ink hover:text-sun-bg transition-colors"
                                                            >
                                                                {p.name.replace(" Powder", "")}
                                                            </Link>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default SourcingMap;
